// Norway-only phone number format / parse for FieldPhoneNumberInput.
//
// Final Form value shape (canonical, what reaches the backend):
//   '+47XXXXXXXX'   — E.164
//
// Display value the user sees in the input (with the +47 rendered as a
// chip to the left, NOT inside the input):
//   'XXX XX XXX'    — national-format spacing, 8 digits max
//
// `parse` takes what the user typed and returns the canonical form.
// `format` takes the canonical form and returns the display form.
//
// The roundtrip is symmetric: format(parse(x)) is what re-renders into
// the input after the user types. We also tolerate users pasting full
// numbers like "+47 12 34 56 78" or "47 12345678" — country-code
// prefix is stripped, last 8 digits kept.

const NO_COUNTRY_PREFIX = '+47';

const digitsOnly = str => (str || '').replace(/\D/g, '');

// PARSE side (user-typed national digits, which may THEMSELVES start with
// "47" — e.g. "47 12 34 56"): only treat a leading "47" as the country code
// when there are more than 8 digits (a full "47" + 8-digit national = 10).
// At <= 8 digits it's the national number, so keep it — stripping it here is
// what previously blocked customers whose number starts with 47.
const stripPastedCountryCode = digits =>
  digits.length > 8 && digits.startsWith('47') ? digits.slice(2) : digits;

// FORMAT side (the stored value is ALWAYS canonical "+47" + national): the
// leading "47" is always the country code, so drop it unconditionally.
const stripCanonicalCountryCode = digits =>
  digits.startsWith('47') ? digits.slice(2) : digits;

// Group 8 digits as "XXX XX XXX" (matches Norwegian phone-book style)
// with graceful intermediate steps as the user types.
const groupNationalDigits = digits => {
  const d = digits.slice(0, 8);
  if (d.length <= 3) {
    return d;
  }
  if (d.length <= 5) {
    return `${d.slice(0, 3)} ${d.slice(3)}`;
  }
  return `${d.slice(0, 3)} ${d.slice(3, 5)} ${d.slice(5)}`;
};

/**
 * Format the canonical E.164 value (`+47XXXXXXXX`) for display in the
 * input. The chip on the left already shows "+47", so the input itself
 * only shows the national 8 digits with spaces.
 *
 * Empty value -> empty string (placeholder shows through).
 */
export const format = value => {
  if (!value) {
    return '';
  }
  const national = stripCanonicalCountryCode(digitsOnly(value));
  return groupNationalDigits(national);
};

/**
 * Parse user input back to the canonical E.164 form.
 *
 * - Strips spaces, dashes, parens, anything non-digit.
 * - Strips a leading "47" if the user pasted a full E.164 number.
 * - Keeps the last 8 national digits.
 * - Always prepends `+47` when at least one digit is present so the
 *   value stored in Final Form is canonical from the first keystroke.
 * - Returns `''` for empty input so the field can be cleared.
 */
export const parse = value => {
  if (!value) {
    return '';
  }
  const national = stripPastedCountryCode(digitsOnly(value)).slice(0, 8);
  if (national.length === 0) {
    return '';
  }
  return `${NO_COUNTRY_PREFIX}${national}`;
};
